import { Component, OnDestroy, OnInit } from '@angular/core';

import { Subject, filter, takeUntil } from 'rxjs';

import { TranslationService } from '@mirage-projects/cw-commons';
import {
    ScanControlPointTypeValue,
    DistanceUnit,
    ImporterConverterOptions,
    PcvizJobAdapter,
    GisJobAdapter,
} from '@mirage-projects/cw-models';
import { DbPcvizService, GisPcvizService, JobPcvizService } from '@mirage-projects/cw-pcviz-core';

import { UploadWizardService } from '../../../services/upload-wizard-service/upload-wizard.service';

export interface GisImporterConversionOptions {
    geometryType: string;
    csvType: ScanControlPointTypeValue;
    ScaleFactor: number;
}

/**
 * Gis import options
 * This component is used as an optional step in the layer creation to set the gis layer options
 */
@Component({
    selector: 'cw-gis-import-options',
    templateUrl: 'gis-import-options.component.html',
    standalone: false,
})
export class GisImportOptionsComponent implements OnInit, OnDestroy {
    csvTypes: string[] = [];
    scaleFactorDefinitions: DistanceUnit[] = [];
    geometryTypes!: Array<{ key: string; value: string }>;
    gisConversion!: GisImporterConversionOptions;
    importerOptions!: ImporterConverterOptions;
    jobAdapter: PcvizJobAdapter | undefined;

    private onDestroy$: Subject<void> = new Subject();
    private wizardTabName = 'WIZARD.TAB_GIS_IMPORTER';

    constructor(
        private dbPcvizService: DbPcvizService,
        private uploadWizardService: UploadWizardService,
        private translationService: TranslationService,
        private jobAdapterService: JobPcvizService,
        private gisService: GisPcvizService
    ) {}

    ngOnInit(): void {
        this.initializeData();
        this.listenToStepChanged();
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    onGisSelectChanged(): void {
        this.updateCsvImport();
    }

    private updateCsvImport(): void {
        this.importerOptions.CSVHeaders = (this.jobAdapter as GisJobAdapter)?.getCsvOptions()[this.gisConversion.csvType];
    }

    private initializeData(): void {
        this.gisConversion = this.uploadWizardService.gisConversion;

        this.jobAdapter = this.jobAdapterService.getJobAdapter();
        if (!this.jobAdapter) {
            return;
        }

        this.scaleFactorDefinitions = this.dbPcvizService.getDistanceUnits();
        this.importerOptions = this.jobAdapter.getImporterOptions();
        if (this.importerOptions.ScaleFactor === undefined) {
            this.importerOptions.ScaleFactor = 1;
        }
        this.gisConversion.ScaleFactor = this.importerOptions.ScaleFactor;

        this.geometryTypes = Object.entries(this.gisService.getGeometryType()).map((item) => {
            return { key: item[0], value: item[1] };
        });
        this.csvTypes = Object.keys((this.jobAdapter as GisJobAdapter)._csvOptions);

        this.updateCsvImport();
    }

    private listenToStepChanged(): void {
        this.uploadWizardService.stepChanged
            .pipe(
                takeUntil(this.onDestroy$),
                filter((wizardComponent) => {
                    const step = this.translationService.translate(this.wizardTabName);
                    return this.uploadWizardService.comparePreviousStep(wizardComponent, step);
                })
            )
            .subscribe((wizardComponent) => this.updateSummary(wizardComponent.selectedIndex));
    }

    private getJobAdapter(): PcvizJobAdapter | undefined {
        return this.jobAdapterService.getJobAdapter();
    }

    private updateSummary(index: number): void {
        const fields = [];

        if (this.isCsvGeometry) {
            fields.push({
                key: 'UPLOAD.DXF_GIS_IMPORTER_SELECT_GEOMETRY_TYPE',
                value: `UPLOAD.CSV_GIS_IMPORTER_CONTROL_POINT_${this.gisConversion.csvType}`,
            });
        } else {
            const geometryTypeLabel =
                this.gisConversion.geometryType !== 'AUTODETECT'
                    ? `CREATE_VECTORLAYER_DIALOG.GEOMETRY_TYPE_${this.gisConversion.geometryType.toUpperCase()}`
                    : 'UPLOAD.DXF_GIS_IMPORTER_AUTODETECT';
            const geometryTypeLabelTranslated = this.translationService.translate(geometryTypeLabel);

            fields.push({
                key: 'UPLOAD.DXF_GIS_IMPORTER_SELECT_GEOMETRY_TYPE',
                value: geometryTypeLabelTranslated,
            });
        }

        fields.push({
            key: 'UPLOAD.CSV_GIS_IMPORTER_SELECT_SCALE_FACTOR',
            value:
                this.scaleFactorDefinitions.find((scaleFactor: DistanceUnit) => scaleFactor.fromMeters === this.gisConversion.ScaleFactor)
                    ?.label ?? '',
        });

        this.uploadWizardService.summary[index] = {
            title: 'WIZARD.TAB_GIS_IMPORTER',
            notice: 'UPLOAD.GIS_IMPORTER_SUMMARY_NOTICE',
            fields,
        };
    }

    get title(): string {
        return this.isCsvGeometry ? 'UPLOAD.CSV_GIS_IMPORTER_OPTIONS' : 'UPLOAD.DXF_GIS_IMPORTER_OPTIONS';
    }

    get isCsvGeometry(): boolean {
        return this.getJobAdapter() ? this.getJobAdapter()?.getDetectedType() === 'Gis:csv' : true;
    }
}
